import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Send } from "lucide-react";
import SEO from "../components/SEO";
import Reveal from "../components/Reveal";
import { t } from "../i18n";
import { staticPath } from "../data";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const API = "/api";

export default function ContactPage() {
  const { lang } = useOutletContext();
  const i = t[lang];
  const [form, setForm] = useState({ name: "", email: "", subject: "general", message: "", website: "" });
  const [sending, setSending] = useState(false);

  const title = lang === "it" ? "Contatti | BoingCars" : "Contact | BoingCars";

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("send failed");
      toast.success(i.contact.success);
      setForm({ name: "", email: "", subject: "general", message: "", website: "" });
    } catch {
      toast.error(i.contact.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <SEO lang={lang} title={title} description={i.contact.sub} pathEn={staticPath("en", "contact")} pathIt={staticPath("it", "contact")} />
      <section className="container-site py-16 md:py-24" data-testid="contact-page">
        <div className="grid gap-14 lg:grid-cols-2">
          <Reveal>
            <h1 className="font-display text-4xl font-extrabold tracking-tighter sm:text-5xl">{i.contact.title}</h1>
            <p className="mt-5 max-w-md text-base leading-relaxed text-muted-foreground md:text-lg">{i.contact.sub}</p>
            <div className="mt-10 rounded-2xl bg-white p-6 ring-1 ring-black/5">
              <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-petrol">
                <Mail className="h-4 w-4" aria-hidden="true" /> {i.contact.direct}
              </p>
              <a
                href="mailto:hello@boingcars.com"
                data-testid="contact-email-link"
                className="mt-2 inline-block font-display text-xl font-bold tracking-tight text-foreground transition-colors duration-200 hover:text-petrol"
              >
                hello@boingcars.com
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <form onSubmit={submit} className="rounded-3xl bg-white p-6 shadow-xl ring-1 ring-black/5 sm:p-8" data-testid="contact-form">
              <div className="grid gap-5">
                <div>
                  <Label htmlFor="contact-name" className="text-sm font-semibold">{i.contact.name}</Label>
                  <Input
                    id="contact-name"
                    data-testid="contact-name-input"
                    required
                    minLength={2}
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-2"
                    autoComplete="name"
                  />
                </div>
                <div>
                  <Label htmlFor="contact-email" className="text-sm font-semibold">{i.contact.email}</Label>
                  <Input
                    id="contact-email"
                    data-testid="contact-email-input"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="mt-2"
                    autoComplete="email"
                  />
                </div>
                <div>
                  <Label className="text-sm font-semibold">{i.contact.subject}</Label>
                  <Select value={form.subject} onValueChange={(v) => setForm({ ...form, subject: v })}>
                    <SelectTrigger data-testid="contact-subject-select" className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(i.contact.subjects).map(([k, v]) => (
                        <SelectItem key={k} value={k} data-testid={`contact-subject-${k}`}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="contact-message" className="text-sm font-semibold">{i.contact.message}</Label>
                  <Textarea
                    id="contact-message"
                    data-testid="contact-message-input"
                    required
                    minLength={10}
                    rows={5}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="mt-2"
                  />
                </div>
                <input
                  type="text"
                  name="website"
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                  className="hidden"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                />
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit-button"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-petrol px-6 py-3.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-petrol-dark disabled:opacity-60"
                >
                  <Send className="h-4 w-4" aria-hidden="true" />
                  {sending ? i.contact.sending : i.contact.send}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
}
